import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { Link as RouterLink } from 'react-router-dom';
import Link from '@material-ui/core/Link';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';

import { routes, formatNumber } from '../../../constants.js/misc';
import { Button, FormControl, FormHelperText, Grid, makeStyles, MenuItem, OutlinedInput, Select, TextField, Typography } from '@material-ui/core';
import CurrencyField from '../../CurrencyField/CurrencyField';
import { Autocomplete } from '@material-ui/lab';
import { getProducts, getProductService } from '../../../services/products';
import {
    getOrderProductSearchMinLength,
    formatOrderProductSearchLabel,
    orderProductSearchFilterOptions,
    buildOrderLineFromSearchProduct,
    getOrderLineOptionGroups,
    orderLineHasVariantOptions,
    resolveOrderLineUnitPrice,
    getSelectedVariantOptions,
    getOrderLineCatalogProduct,
    mergeOrderLineRow,
    enrichOrderLineWithCatalog,
    resolveUnitPriceFromCatalogProduct,
    getOrderLineSku,
} from '../../../constants.js/orderProductSearch';
import {
    findProductVariant,
    getLineUnitPrice,
    normalizeOptionVariants,
    resolveUnitPrice,
} from '../../../pages/CreateOrder/createOrderPricing';

// Assets
import deleteIcon from "../../../assets/icons/deleteIcon.svg";
import { Controller, useWatch } from 'react-hook-form';
import { DivContainerTable } from '../../../constants.js/containers';
import styled from '@emotion/styled';

const useStyles = makeStyles({
    container: {
        display: 'block',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        padding: '0px 16px',
    },
    card: {
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        flexDirection: 'column',
        padding: 26,
        marginBottom: 30
    },
    title: {
        fontSize: '22px',

    },
    header: {
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'center',
        marginBottom: '1rem',
        width: '100%',
    },
    button: {
        display: 'none',
    },
    numberFieldIcon: {
        width: '10%',
        color: '#48AC98',
        lineHeight: 0,
    },
    numberFieldCode: {
        width: '15%',
    },
    numberFieldWhatsapp: {
        width: '85%',
        textAlign: 'center',
        paddingLeft: 10,
        paddingRight: 10,
        margin: 0,
    },
    total: {
        padding: '16px',
        display: 'flex',
        width: '100%',
        justifyContent: 'flex-end'
    },
});

const Helper = styled(FormControl)`
    &:focus-within {
        p {
            visibility: visible;
            color: #53627C;
            transition: all 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55);
        };
        &:hover {
            p {
                font-size: 1rem;
                transition: all 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55);
            };
        };
        
    }
`;

const OrderProductEditRow = ({
    index,
    field,
    control,
    register,
    classes,
    t,
    lineMetaByFieldIdRef,
    getCatalogProductById,
    onVariantChange,
    onRemove,
    onSaveClick,
    handleSubtotal,
    liveUnitPrice,
}) => {
    const lineValues = useWatch({ control, name: `products.${index}` });
    const variantSelections = useWatch({ control, name: `products.${index}.variants` });

    const selectedVariants = useMemo(
        () => normalizeOptionVariants(variantSelections),
        [variantSelections]
    );

    const catalogProduct = useMemo(() => {
        const meta = field?.id ? lineMetaByFieldIdRef?.current?.[field.id] : null;
        const productId = field?.product_id ?? field?.product?.id;
        return getCatalogProductById(productId) ?? meta?.catalogProduct ?? null;
    }, [field, getCatalogProductById, lineMetaByFieldIdRef, lineValues]);

    const row = useMemo(() => {
        const meta = field?.id ? lineMetaByFieldIdRef?.current?.[field.id] : null;
        const merged = mergeOrderLineRow(
            { ...field, ...meta, variants: selectedVariants },
            lineValues
        );
        return enrichOrderLineWithCatalog(merged, catalogProduct);
    }, [field, lineValues, selectedVariants, catalogProduct, lineMetaByFieldIdRef]);

    const unitPrice = useMemo(() => {
        if (liveUnitPrice != null && !Number.isNaN(Number(liveUnitPrice))) {
            return Number(liveUnitPrice);
        }
        const fromCatalog = resolveUnitPriceFromCatalogProduct(catalogProduct, selectedVariants);
        if (fromCatalog != null) {
            return fromCatalog;
        }
        return resolveOrderLineUnitPrice({ ...row, variants: selectedVariants });
    }, [liveUnitPrice, catalogProduct, selectedVariants, row]);
    const quantity = Number(lineValues?.quantity ?? row?.quantity) || 0;

    return (
        <TableRow key={field.id ?? index}>
            <TableCell className={classes.centerTable}>
                <Link
                    component={RouterLink}
                    to={routes.updateProduct + '/' + (field?.product?.id ?? field?.product_id)}
                    color="primary"
                >
                    {field?.product?.name || field?.name}
                </Link>
            </TableCell>

            <TableCell>
                {!orderLineHasVariantOptions(row) ? (
                    <>{t('OrdersTablePage.products.noVariants')}</>
                ) : (
                    <Grid container spacing={1}>
                        {getOrderLineOptionGroups(row).map((variant, optionIndex) => {
                            const currentValue = selectedVariants[optionIndex] ?? '';
                            return (
                                <Grid item xs={6} key={variant?.id ?? optionIndex}>
                                    <Controller
                                        control={control}
                                        name={`products.${index}.variants.${optionIndex}`}
                                        defaultValue={currentValue}
                                        render={({ field: variantField }) => (
                                            <Select
                                                value={variantField.value ?? ''}
                                                id="variants"
                                                onChange={(event) => {
                                                    const nextValue = event.target.value;
                                                    variantField.onChange(nextValue);
                                                    onVariantChange(index, optionIndex, nextValue);
                                                }}
                                                input={<OutlinedInput fullWidth />}
                                            >
                                                {variant?.options?.map((option, menuIndex) => {
                                                    const optionLabel =
                                                        option?.name ?? option?.value ?? option;
                                                    return (
                                                        <MenuItem key={menuIndex} value={optionLabel}>
                                                            {optionLabel}
                                                        </MenuItem>
                                                    );
                                                })}
                                            </Select>
                                        )}
                                    />
                                </Grid>
                            );
                        })}
                    </Grid>
                )}
            </TableCell>

            <TableCell>{getOrderLineSku(row)}</TableCell>

            <TableCell>
                <input type="hidden" {...register(`products.${index}.price`)} />
                <TextField
                    {...register(`products.${index}.quantity`)}
                    onClick={handleSubtotal}
                    onKeyUp={handleSubtotal}
                    variant="outlined"
                    defaultValue={1}
                    InputProps={{ inputProps: { min: 1 } }}
                    type="number"
                    style={{ width: 70 }}
                />
            </TableCell>

            <TableCell>{formatNumber(unitPrice)}</TableCell>
            <TableCell>{formatNumber(unitPrice * quantity)}</TableCell>
            <TableCell onClick={onSaveClick}>
                <img
                    src={deleteIcon}
                    alt="delete"
                    onClick={() => onRemove({ index })}
                    style={{ cursor: 'pointer' }}
                />
            </TableCell>
        </TableRow>
    );
};

const OrderProductsTableToEdit = ({
    deliveryCost,
    storeData,
    control,
    register,
    watch,
    handleSubmit,
    setValueShipping,
    setFormValue,
    useFieldArray,
    saveInfo,
    submitProductsTable,
    rememberProducts = () => {},
    productCatalog = {},
    productCatalogRef = null,
    lineMetaByFieldIdRef = null,
    fieldRowsRef = null,
}) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const [value, setValue] = useState(null);
    const [inputValue, setInputValue] = useState('');
    const [subtotal, setSubtotal] = useState(0);
    const [openProduct, setOpenProduct] = useState(false);
    const [productOptions, setProductOptions] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [liveUnitPrices, setLiveUnitPrices] = useState({});
    const pendingLineMetaRef = useRef(null);

    const getCatalogProductById = useCallback(
        (productId) => {
            if (productId == null) return null;
            if (productCatalogRef?.current) {
                return (
                    productCatalogRef.current[productId] ??
                    productCatalogRef.current[String(productId)] ??
                    null
                );
            }
            return productCatalog[productId] ?? productCatalog[String(productId)] ?? null;
        },
        [productCatalog, productCatalogRef]
    );

    const { fields, append, remove } = useFieldArray({
        control: control,
        name: 'products',
    });

    const handleAppend = useCallback(
        async (product) => {
            if (!product?.id || !storeData?.id) return;

            let fullProduct = product;
            try {
                const res = await getProductService(storeData.id, product.id);
                if (res?.data) {
                    fullProduct = { ...product, ...res.data };
                }
            } catch (err) {
                console.log(err);
            }

            rememberProducts([fullProduct]);
            const line = buildOrderLineFromSearchProduct(fullProduct, inputValue);
            pendingLineMetaRef.current = {
                variantsProduct: line.variantsProduct ?? [],
                optionsProduct: line.optionsProduct ?? [],
                catalogProduct: fullProduct,
            };
            append(line);
            const nextIndex = fields.length;
            setFormValue(`products.${nextIndex}.variants`, line.variants ?? [], { shouldDirty: true });
            setFormValue(`products.${nextIndex}.variantsProduct`, line.variantsProduct ?? [], {
                shouldDirty: true,
            });
            setFormValue(`products.${nextIndex}.optionsProduct`, line.optionsProduct ?? [], {
                shouldDirty: true,
            });
            setLiveUnitPrices((prev) => ({
                ...prev,
                [nextIndex]: line.price,
            }));
        },
        [append, inputValue, storeData?.id, rememberProducts, fields.length, setFormValue]
    );

    const handleRemove = useCallback(({ index }) => {
        const fieldId = fields[index]?.id;
        if (fieldId && lineMetaByFieldIdRef?.current) {
            delete lineMetaByFieldIdRef.current[fieldId];
        }
        remove(index);
        setLiveUnitPrices((prev) => {
            const next = { ...prev };
            delete next[index];
            const reindexed = {};
            Object.keys(next).forEach((key) => {
                const keyNum = Number(key);
                if (keyNum > index) {
                    reindexed[keyNum - 1] = next[key];
                } else if (keyNum < index) {
                    reindexed[keyNum] = next[key];
                }
            });
            return reindexed;
        });
    }, [remove, fields, lineMetaByFieldIdRef]);

    const watchedProducts = useWatch({ control, name: 'products' }) ?? [];

    useEffect(() => {
        if (fieldRowsRef) {
            fieldRowsRef.current = fields;
        }
    }, [fields, fieldRowsRef]);

    useEffect(() => {
        if (!pendingLineMetaRef.current || !lineMetaByFieldIdRef?.current || !fields.length) {
            return;
        }
        const lastField = fields[fields.length - 1];
        if (lastField?.id) {
            lineMetaByFieldIdRef.current[lastField.id] = pendingLineMetaRef.current;
            pendingLineMetaRef.current = null;
        }
    }, [fields, lineMetaByFieldIdRef]);

    const getLineWithCatalog = useCallback(
        (index) => {
            const field = fields[index];
            const meta = field?.id ? lineMetaByFieldIdRef?.current?.[field.id] : null;
            const merged = mergeOrderLineRow({ ...field, ...meta }, watchedProducts?.[index]);
            const productId = merged.product_id ?? merged.product?.id;
            const catalogProduct =
                getCatalogProductById(productId) ?? meta?.catalogProduct ?? null;
            return enrichOrderLineWithCatalog(merged, catalogProduct);
        },
        [fields, watchedProducts, getCatalogProductById, lineMetaByFieldIdRef]
    );

    const resolveRowUnitPrice = useCallback(
        (row, selectedOverride) => {
            const selected = selectedOverride ?? getSelectedVariantOptions(row);
            const productId = row?.product_id ?? row?.product?.id;
            const catalogProduct = getCatalogProductById(productId);
            const fromCatalog = resolveUnitPriceFromCatalogProduct(catalogProduct, selected);
            if (fromCatalog != null) {
                return fromCatalog;
            }
            return resolveOrderLineUnitPrice({ ...row, variants: selected });
        },
        [getCatalogProductById]
    );

    const handleSubtotal = useCallback(() => {
        let nextSubtotal = 0;
        for (let i = 0; i < fields.length; i++) {
            const row = getLineWithCatalog(i);
            const quantity = Number(row?.quantity) || 0;
            const unitPrice =
                liveUnitPrices[i] ??
                resolveRowUnitPrice(row, normalizeOptionVariants(watchedProducts?.[i]?.variants));
            nextSubtotal += unitPrice * quantity;
        }
        setSubtotal(nextSubtotal);
    }, [fields.length, getLineWithCatalog, resolveRowUnitPrice, liveUnitPrices, watchedProducts]);

    const applyLinePricing = useCallback(
        (index, selectedOverride) => {
            if (!setFormValue) return;
            const field = fields[index];
            const meta = field?.id ? lineMetaByFieldIdRef?.current?.[field.id] : null;
            const row = getLineWithCatalog(index);
            const selected =
                selectedOverride != null
                    ? normalizeOptionVariants(selectedOverride)
                    : getSelectedVariantOptions(row);
            const productId = row.product_id ?? row.product?.id;
            const catalogProduct =
                getCatalogProductById(productId) ?? meta?.catalogProduct ?? null;
            if (!catalogProduct) return;

            const catalog = {
                id: catalogProduct.id,
                price: catalogProduct.price,
                discount_price: catalogProduct.discount_price ?? catalogProduct.discountPrice,
                product_variants:
                    catalogProduct.product_variants ?? catalogProduct.productVariants ?? [],
                product_options:
                    catalogProduct.product_options ?? catalogProduct.productOptions ?? [],
            };
            if (!catalog.product_variants?.length) {
                return;
            }

            const { unitPrice, discountPrice } = resolveUnitPrice(catalog, selected);
            const linePrice = getLineUnitPrice(unitPrice, discountPrice);
            setFormValue(`products.${index}.price`, linePrice, { shouldDirty: true });
            const matched = findProductVariant(catalog, selected);
            if (matched?.id != null) {
                setFormValue(`products.${index}.product_variant_id`, matched.id, { shouldDirty: true });
            }
            handleSubtotal();
        },
        [fields, getLineWithCatalog, getCatalogProductById, lineMetaByFieldIdRef, setFormValue, handleSubtotal]
    );

    const handleVariantChange = useCallback(
        (index, optionIndex, nextValue) => {
            const field = fields[index];
            const meta = field?.id ? lineMetaByFieldIdRef?.current?.[field.id] : null;
            const productId = field?.product_id ?? field?.product?.id;
            const catalogProduct =
                getCatalogProductById(productId) ?? meta?.catalogProduct ?? null;
            const merged = mergeOrderLineRow({ ...field, ...meta }, watchedProducts?.[index]);
            const row = enrichOrderLineWithCatalog(merged, catalogProduct);
            const groups = getOrderLineOptionGroups(row);
            const current = normalizeOptionVariants(watchedProducts?.[index]?.variants);
            const selected = groups.map((group, i) => {
                if (i === optionIndex) return nextValue;
                if (current[i] != null && current[i] !== '') return current[i];
                return group?.options?.[0]?.name ?? group?.options?.[0]?.value ?? '';
            });

            setFormValue(`products.${index}.variants`, selected, {
                shouldDirty: true,
                shouldTouch: true,
            });
            setFormValue(`products.${index}.variants.${optionIndex}`, nextValue, {
                shouldDirty: true,
                shouldTouch: true,
            });

            const linePrice = resolveUnitPriceFromCatalogProduct(catalogProduct, selected);
            if (linePrice != null) {
                setLiveUnitPrices((prev) => ({ ...prev, [index]: linePrice }));
                setFormValue(`products.${index}.price`, linePrice, {
                    shouldDirty: true,
                    shouldTouch: true,
                });
                const catalog = {
                    id: catalogProduct.id,
                    price: catalogProduct.price,
                    discount_price:
                        catalogProduct.discount_price ?? catalogProduct.discountPrice,
                    product_variants:
                        catalogProduct.product_variants ?? catalogProduct.productVariants ?? [],
                };
                const matched = findProductVariant(catalog, selected);
                if (matched?.id != null) {
                    setFormValue(`products.${index}.product_variant_id`, matched.id, {
                        shouldDirty: true,
                        shouldTouch: true,
                    });
                }
            }

            handleSubtotal();
        },
        [
            fields,
            watchedProducts,
            lineMetaByFieldIdRef,
            getCatalogProductById,
            setFormValue,
            handleSubtotal,
        ]
    );

    useEffect(() => {
        handleSubtotal();
    }, [fields, watchedProducts, liveUnitPrices, handleSubtotal]);

    useEffect(() => {
        const subscription = watch((_, { name }) => {
            if (!name?.startsWith('products')) return;
            if (name.includes('.quantity')) {
                handleSubtotal();
            }
        });
        return () => subscription.unsubscribe();
    }, [watch, handleSubtotal]);

    useEffect(() => {
        if (!storeData?.id || !fields.length || !setFormValue) return undefined;

        let cancelled = false;
        fields.forEach((field, index) => {
            const productId = field?.product_id ?? field?.product?.id;
            const meta = field?.id ? lineMetaByFieldIdRef?.current?.[field.id] : null;
            const hasVariantCatalog =
                (field?.variantsProduct?.length ?? 0) > 0 ||
                (field?.product?.product_variants?.length ?? 0) > 0 ||
                (meta?.variantsProduct?.length ?? 0) > 0 ||
                (getCatalogProductById(productId)?.product_variants?.length ?? 0) > 0;
            if (!productId || hasVariantCatalog) return;

            getProductService(storeData.id, productId)
                .then((res) => {
                    if (cancelled || !res?.data) return;
                    const product = res.data;
                    rememberProducts([product]);
                    if (field?.id && lineMetaByFieldIdRef?.current) {
                        lineMetaByFieldIdRef.current[field.id] = {
                            ...(lineMetaByFieldIdRef.current[field.id] ?? {}),
                            variantsProduct: product.product_variants ?? [],
                            optionsProduct: product.product_options ?? [],
                            catalogProduct: product,
                        };
                    }
                    if (product.product_variants?.length) {
                        setFormValue(`products.${index}.variantsProduct`, product.product_variants, {
                            shouldDirty: false,
                        });
                    }
                    if (product.product_options?.length) {
                        setFormValue(`products.${index}.optionsProduct`, product.product_options, {
                            shouldDirty: false,
                        });
                        setFormValue(
                            `products.${index}.product.product_options`,
                            product.product_options,
                            { shouldDirty: false }
                        );
                    }
                    applyLinePricing(index);
                })
                .catch((err) => console.log(err));
        });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fields.length, storeData?.id]);

    useEffect(() => {
        if (value?.id) {
            handleAppend(value);
            setValue(null);
        }
    }, [value, handleAppend]);

    useEffect(() => {
        const term = inputValue.trim();
        const minLength = getOrderProductSearchMinLength(term);
        if (term.length < minLength || !storeData?.id) {
            setProductOptions([]);
            setSearchLoading(false);
            return undefined;
        }

        setSearchLoading(true);
        const timer = window.setTimeout(() => {
            getProducts(storeData.id, term)
                .then((res) => {
                    setProductOptions(Array.isArray(res?.data) ? res.data : []);
                })
                .catch((err) => {
                    console.log(err);
                    setProductOptions([]);
                })
                .finally(() => setSearchLoading(false));
        }, 300);

        return () => window.clearTimeout(timer);
    }, [storeData?.id, inputValue]);


    // Fixed delivery cost calculation
    const getDeliveryCost = () => {
        // Fix: deliveryCost !== (0 || null) will always be true when deliveryCost is not 0
        // because (0 || null) evaluates to null, and any number !== null
        if (deliveryCost && deliveryCost !== 0) {
            return deliveryCost;
        }

        if (storeData?.delivery_cost_enabled) {
            const deliveryCostValue = storeData?.delivery_cost_details;
            // Check if it's a valid number (string or number)
            if (deliveryCostValue !== undefined && deliveryCostValue !== null && deliveryCostValue !== '') {
                const parsedValue = Number(deliveryCostValue);
                return isNaN(parsedValue) ? 0 : parsedValue;
            }
        }

        return 0;
    }

    const handleFormChange = (event) => {
        if (event?.target?.dataset?.orderProductSearch === 'true') {
            return;
        }
        handleSubmit(saveInfo)(event);
    };

    return (
        <form onSubmit={handleSubmit(saveInfo)} onLoad={handleSubmit(saveInfo)} onChange={handleFormChange} className={classes.container}>
            <Typography style={{ marginTop: 18 }}>
                {t('OrdersTablePage.searchProduct')}
            </Typography>
            <Autocomplete
                value={value}
                onChange={(e, selected) => setValue(selected)}
                inputValue={inputValue}
                onInputChange={(event, newInputValue, reason) => {
                    if (reason === 'reset') {
                        setInputValue('');
                        return;
                    }
                    setInputValue(newInputValue);
                }}
                style={{ width: '100%' }}
                open={openProduct}
                loading={searchLoading}
                onOpen={() => {
                    setOpenProduct(true);
                }}
                onClose={() => {
                    setOpenProduct(false);
                }}
                getOptionSelected={(option, selected) => option.id === selected.id}
                getOptionLabel={(option) => formatOrderProductSearchLabel(option, inputValue)}
                filterOptions={orderProductSearchFilterOptions}
                options={productOptions}
                noOptionsText={
                    inputValue.trim().length >= getOrderProductSearchMinLength(inputValue)
                        ? 'Sin resultados'
                        : t('OrdersTablePage.searchProduct.anotation')
                }
                renderInput={(params) => (
                    <Helper>
                        <FormHelperText>
                            **{t('OrdersTablePage.searchProduct.anotation')}
                        </FormHelperText>
                        <TextField
                            {...params}
                            variant="outlined"
                            placeholder="Buscar por nombre o SKU"
                            inputProps={{
                                ...params.inputProps,
                                'data-order-product-search': 'true',
                            }}
                        />

                    </Helper>
                )}
            />
            <DivContainerTable>
                <Table style={{ backgroundColor: '#F8F9FB', marginTop: 32 }} stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('OrdersTablePage.products.headers.product')}</TableCell>
                            <TableCell>{t('OrdersTablePage.headers.variants')}</TableCell>
                            <TableCell>{t('OrdersTablePage.products.headers.sku')}</TableCell>
                            <TableCell>{t('OrdersTablePage.products.headers.amount')}</TableCell>
                            <TableCell>{t('OrdersTablePage.products.headers.price')}</TableCell>
                            <TableCell>{t('OrdersTablePage.products.headers.total')}</TableCell>
                            <TableCell></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {fields.map((product, index) => (
                            <OrderProductEditRow
                                key={product.id ?? index}
                                index={index}
                                field={product}
                                control={control}
                                register={register}
                                classes={classes}
                                t={t}
                                lineMetaByFieldIdRef={lineMetaByFieldIdRef}
                                getCatalogProductById={getCatalogProductById}
                                onVariantChange={handleVariantChange}
                                onRemove={handleRemove}
                                onSaveClick={handleSubmit(saveInfo)}
                                handleSubtotal={handleSubtotal}
                                liveUnitPrice={liveUnitPrices[index]}
                            />
                        ))}
                    </TableBody>
                </Table>
            </DivContainerTable>
            <div className={classes.total}>
                <div style={{ marginRight: '1rem', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                    <Typography gutterBottom variant="body1">Subtotal:</Typography>
                    <Typography gutterBottom variant="body1">Costos de envio:</Typography>
                    <Typography gutterBottom variant="body1">Total:</Typography>
                </div>
                <div>
                    <Typography gutterBottom variant="body1" style={{ textAlign: 'right' }}>
                        {formatNumber(subtotal)}
                    </Typography>
                    <Typography gutterBottom variant="body1">
                        <Controller
                            render={({ field }) => (
                                <CurrencyField
                                    {...field}
                                    setValue={val => setValueShipping('shipping_cost', val)}
                                    style={{ width: '120px', textAlignLast: 'right' }}
                                    defaultValue={getDeliveryCost()}
                                    variant="standard"
                                />
                            )}
                            name="shipping_cost"
                            control={control}
                            rules={{ required: true }}
                            defaultValue={getDeliveryCost()}
                        />
                    </Typography>
                    <Typography gutterBottom variant="body1" style={{ textAlign: 'right' }}>
                        {formatNumber((subtotal || 0) + Number(watch('shipping_cost') || 0))}
                    </Typography>
                </div>
            </div>
            <Button
                className={classes.button}
                fullWidth
                ref={submitProductsTable}
                type='submit'
                variant="contained"
                color="primary">
                {t('OrderDetail.saveBtn')}
            </Button>
        </form>
    );
};

const mapStateToProps = ({ common }) => ({
    storeData: common.storeData
});

export default connect(
    mapStateToProps
)(OrderProductsTableToEdit);